import React, { useState, useRef, useEffect } from "react";
import BeaconMapperView from "./BeaconMapperlayout";
import axiosInstance from "../../utils/axios";
import {
  DRAG_THRESHOLD,
  TRIPLE_CLICK_TIMEOUT,
} from "../../components/templateComponents/constants";
import * as XLSX from "xlsx";
import "./ImageMapper.css";
import JSZip from "jszip";

const ImageMapper = ({ selectedFloorId }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [markers, setMarkers] = useState([]);
  const [beaconTypes, setBeaconTypes] = useState({});
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [isDraggingLine, setIsDraggingLine] = useState(false);
  const [dragStartMarker, setDragStartMarker] = useState(null);
  const [currentDragLineEnd, setCurrentDragLineEnd] = useState(null);
  const [connections, setConnections] = useState([]);
  const imgRef = useRef(null);
  const imageContainerRef = useRef(null);
  const nextMarkerId = useRef(1);
  const justFinishedDragRef = useRef(false);
  const [markerIdForRemovalConfirmation, setMarkerIdForRemovalConfirmation] =
    useState(null);
  const [
    connectionIdForRemovalConfirmation,
    setConnectionIdForRemovalConfirmation,
  ] = useState(null);
  const [potentialDragStart, setPotentialDragStart] = useState(null);
  const [showClearAllConfirmation, setShowClearAllConfirmation] =
    useState(false);
  const clickSequenceRef = useRef({ markerId: null, count: 0, timer: null });
  const [maxNeighborsToConnect, setMaxNeighborsToConnect] = useState(4);
  const canvasRef = useRef(null);
  const [pointInputMarkerId, setPointInputMarkerId] = useState(null);
  const [availableBeaconsCount, setAvailableBeaconsCount] = useState(0);
  // New state for available beacons data
  const [availableBeaconsData, setAvailableBeaconsData] = useState([]);
  // New state to control beacon name dropdown visibility
  const [showBeaconNameDropdownForMarkerId, setShowBeaconNameDropdownForMarkerId] = useState(null);


  useEffect(() => {
    const fetchBeaconTypes = async () => {
      try {
        const res = await axiosInstance.post("/view_beacon_type/v1.1", {
          view_type: "view",
        });
        const data = res.data;
        if (data.Status === "Success" && data.Data) {
          setBeaconTypes(data.Data);
        } else {
          console.error("Failed to fetch beacon types:", data.Message);
          setBeaconTypes({});
        }
      } catch (error) {
        console.error("Error fetching beacon types:", error);
        setBeaconTypes({});
      }
    };

    fetchBeaconTypes();
  }, []);


  const handlePointValueSubmit = (markerId, pointValue) => {
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) =>
        marker.id === markerId ? { ...marker, point: pointValue } : marker
      )
    );
    setPointInputMarkerId(null);
    // After submitting point value, show the beacon name dropdown for this marker
    setShowBeaconNameDropdownForMarkerId(markerId);
  };

  // New handler for beacon name selection
  const handleBeaconNameChange = (markerId, becId, becName) => {
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) =>
        marker.id === markerId ? { ...marker, bec_id: becId, bec_name: becName } : marker
      )
    );
    setShowBeaconNameDropdownForMarkerId(null); // Hide dropdown after selection
  };


  useEffect(() => {
    if (selectedFloorId) {
      const fetchAvailableBeacons = async () => {
        try {
          const res = await axiosInstance.post("/view_beacon_master/v1.1", {
            view_type: "view all",
            pairing_status: "unpaired",
          });
          if (res.data.Status === "Success") {
            setAvailableBeaconsCount(res.data.Data.length);
            setAvailableBeaconsData(res.data.Data); // Store the full data
            console.log(
              `Fetched ${res.data.Data.length} available beacons to plot.`
            );
          } else {
            console.error("Error fetching available beacons:", res.data.Message);
            setAvailableBeaconsCount(0);
            setAvailableBeaconsData([]);
          }
        } catch (err) {
          console.error("API error fetching beacons:", err);
          setAvailableBeaconsCount(0);
          setAvailableBeaconsData([]);
        }
      };

      // Clear old markers when floor changes
      setMarkers([]);
      nextMarkerId.current = 1;

      fetchAvailableBeacons();
    }
  }, [selectedFloorId]);


  const clearAllBeacons = () => {
    setMarkers([]);
    setConnections([]);
    nextMarkerId.current = 1;
    setActiveMarkerId(null);
    setDragStartMarker(null);
    setCurrentDragLineEnd(null);
    setPotentialDragStart(null);
    setMarkerIdForRemovalConfirmation(null);
    setConnectionIdForRemovalConfirmation(null);
    setShowClearAllConfirmation(false);
    setShowBeaconNameDropdownForMarkerId(null); // Clear beacon name dropdown state
  };

  const handleClearAllClick = () => {
    if (markers.length > 0) {
      setShowClearAllConfirmation(true);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      if (file.size > 500 * 1024) {
        alert("Image size exceeds 500KB. Please choose a smaller file.");
        event.target.value = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
        setMarkers([]);
        nextMarkerId.current = 1;
        setActiveMarkerId(null);
        setConnections([]);
        setIsDraggingLine(false);
        setDragStartMarker(null);
        setCurrentDragLineEnd(null);
        setPotentialDragStart(null);
        justFinishedDragRef.current = false;
        setMarkerIdForRemovalConfirmation(null);
        setConnectionIdForRemovalConfirmation(null);
        setShowBeaconNameDropdownForMarkerId(null); // Clear beacon name dropdown state
        if (clickSequenceRef.current.timer)
          clearTimeout(clickSequenceRef.current.timer);
        clickSequenceRef.current = { markerId: null, count: 0, timer: null };
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (imageSrc && imgRef.current) {
      const img = imgRef.current;
      const handleLoad = () => {
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        let displayWidth = naturalWidth;
        let displayHeight = naturalHeight;

        if (naturalWidth > 610) {
          displayWidth = 610;
          displayHeight = (naturalHeight / naturalWidth) * 610;
        }
        setImageDimensions({ width: displayWidth, height: displayHeight });
        img.dataset.naturalWidth = naturalWidth;
        img.dataset.naturalHeight = naturalHeight;
      };

      img.onload = handleLoad;
      if (img.complete) {
        handleLoad();
      }
    }
  }, [imageSrc]);

  const getMousePositionOnImage = (event) => {
    if (!imageContainerRef.current) return null;
    const rect = imageContainerRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handleMarkerMouseDown = (marker, event) => {
    event.stopPropagation();
    if (!imgRef.current) return;
    justFinishedDragRef.current = false;

    if (
      markerIdForRemovalConfirmation !== null &&
      markerIdForRemovalConfirmation !== marker.id
    ) {
      setMarkerIdForRemovalConfirmation(null);
    }
    if (connectionIdForRemovalConfirmation !== null) {
      setConnectionIdForRemovalConfirmation(null);
    }
    setShowBeaconNameDropdownForMarkerId(null); // Close beacon name dropdown on new mousedown
    if (clickSequenceRef.current.timer)
      clearTimeout(clickSequenceRef.current.timer); // Keep this to cancel timer on new mousedown

    const naturalWidth = parseFloat(imgRef.current.dataset.naturalWidth || 1);
    const naturalHeight = parseFloat(imgRef.current.dataset.naturalHeight || 1);
    const displayX = (marker.x / naturalWidth) * imageDimensions.width;
    const displayY = (marker.y / naturalHeight) * imageDimensions.height;

    setPotentialDragStart({
      marker,
      clientX: event.clientX,
      clientY: event.clientY,
      displayX,
      displayY,
    });
  };

  const handleImageContainerMouseMove = (event) => {
    if (potentialDragStart) {
      const dx = event.clientX - potentialDragStart.clientX;
      const dy = event.clientY - potentialDragStart.clientY;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
        if (clickSequenceRef.current.timer)
          clearTimeout(clickSequenceRef.current.timer); // Cancel click sequence if drag starts
        clickSequenceRef.current = { markerId: null, count: 0, timer: null };

        setIsDraggingLine(true);
        setDragStartMarker({
          id: potentialDragStart.marker.id,
          x: potentialDragStart.displayX,
          y: potentialDragStart.displayY,
        });
        const currentPosOnImage = getMousePositionOnImage(event);
        if (currentPosOnImage) setCurrentDragLineEnd(currentPosOnImage);
        else
          setCurrentDragLineEnd({
            x: potentialDragStart.displayX,
            y: potentialDragStart.displayY,
          });
        setActiveMarkerId(null); // Drag started, close any open dropdown
        setShowBeaconNameDropdownForMarkerId(null); // Close beacon name dropdown on drag
        setPotentialDragStart(null);
        if (markerIdForRemovalConfirmation === potentialDragStart.marker.id) {
          setMarkerIdForRemovalConfirmation(null);
        }
        if (connectionIdForRemovalConfirmation !== null) {
          setConnectionIdForRemovalConfirmation(null);
        }
      }
    } else if (isDraggingLine && dragStartMarker) {
      const pos = getMousePositionOnImage(event);
      if (pos) setCurrentDragLineEnd(pos);
    }
  };

  const handleMarkerMouseUp = (targetMarker, event) => {
    event.stopPropagation();

    let dragJustFinished = false; // Local flag for this specific mouseup event
    if (isDraggingLine && dragStartMarker) {
      if (dragStartMarker.id !== targetMarker.id) {
        const connectionId = `${dragStartMarker.id}-${targetMarker.id}`;
        const reverseConnectionId = `${targetMarker.id}-${dragStartMarker.id}`;
        const existingConnection = connections.find(
          (c) => c.id === connectionId || c.id === reverseConnectionId
        );
        if (!existingConnection) {
          setConnections((prev) => [
            ...prev,
            { id: connectionId, from: dragStartMarker.id, to: targetMarker.id },
          ]);
        }
      }
      dragJustFinished = true; // A drag operation concluded here
    }

    setIsDraggingLine(false);
    setDragStartMarker(null);
    setCurrentDragLineEnd(null);
    setPotentialDragStart(null);

    if (dragJustFinished) {
      justFinishedDragRef.current = true;
      setTimeout(() => (justFinishedDragRef.current = false), 0);
    }
  };

  const handleImageContainerMouseUp = (event) => {
    let dragJustFinished = false; // Local flag
    if (isDraggingLine) {
      // If a drag was in progress and ends on container (cancelled drag)
      dragJustFinished = true;
    }
    setIsDraggingLine(false);
    setDragStartMarker(null);
    setCurrentDragLineEnd(null);
    setPotentialDragStart(null);

    if (dragJustFinished) {
      justFinishedDragRef.current = true;
      setTimeout(() => (justFinishedDragRef.current = false), 0);
    }
  };

  const handleAddRow = () => {
    if (markers.length >= availableBeaconsCount) {
      alert(`Cannot add more beacons. Maximum allowed: ${availableBeaconsCount}`);
      return null; // Stop execution
    }

    const newMarker = {
      id: nextMarkerId.current,
      x: 0, // Default coordinates (to be updated later)
      y: 0,
      type: "",
      connections: [],
      potentialConnections: [],
      source_node: `B${nextMarkerId.current.toString().padStart(3, "0")}`,
      neighbors_node: "",
      bec_hex_identifier: "",
      bec_floor_id: (selectedFloorId || "").toString().padStart(2, "0"),
      bec_typ_id: "",
      bex_pt_map_X: "",
      bex_pt_map_Y: "",
      status_id: 1,
      bec_est_id: "",
      reserved: "",
      bec_namespace_key: "",
      bec_instance_key: `BEC_${Date.now()}`,
      wayter_id: "1",
      isNew: true,
      point: "", // Initialize point
      bec_id: null, // Initialize bec_id for available beacon
      bec_name: "", // Initialize bec_name for available beacon
    };

    setMarkers((prevMarkers) => [...prevMarkers, newMarker]);
    nextMarkerId.current += 1;

    return { id: newMarker.id, field: "bex_pt_map_X" };
  };


  // Handle updating a marker's properties
  const handleUpdateMarker = (id, field, value) => {
    console.log(
      `[handleUpdateMarker] Updating marker ${id}: ${field} = ${value}`
    );

    setMarkers((prevMarkers) => {
      const updatedMarkers = prevMarkers.map((marker) => {
        if (marker.id !== id) return marker;

        // Create a new marker object with the updated field
        const updatedMarker = { ...marker };

        // Update the field that was changed
        updatedMarker[field] = value;

        // Handle coordinate fields
        if (field === "bex_pt_map_X" || field === "bex_pt_map_Y") {
          // For coordinate fields, ensure we have a valid number
          const numValue = Number(value);
          if (!isNaN(numValue)) {
            updatedMarker[field] = numValue;
            // Also update the x/y position if it's a coordinate field
            if (field === "bex_pt_map_X") {
              updatedMarker.x = numValue;
            } else if (field === "bex_pt_map_Y") {
              updatedMarker.y = numValue;
            }
            console.log(
              `[handleUpdateMarker] Updated coordinates: x=${updatedMarker.x}, y=${updatedMarker.y}`
            );
          }
        }

        // Handle the 'point' field specifically
        else if (field === "points") { // Changed from 'point' to 'points' to match BeaconTable's usage
          updatedMarker.point = value; // Update the 'point' property
        }

        // Handle wayter_id field
        else if (field === "wayter_id") {
          // Ensure wayter_id is always a string
          updatedMarker.wayter_id = String(value);
        }
        else if (field === "reserved") {
          updatedMarker[field] = value.slice(0, 4);
        }

        console.log(`[handleUpdateMarker] Updated marker ${id} - ${field}:`, {
          before: marker[field],
          after: value,
          marker: updatedMarker,
        });

        return updatedMarker;
      });

      console.log(
        "[handleUpdateMarker] Updated markers array:",
        updatedMarkers
      );
      return updatedMarkers;
    });
  };

  const handleDeleteMarker = (id) => {
    // Remove the marker
    setMarkers((prevMarkers) =>
      prevMarkers.filter((marker) => marker.id !== id)
    );

    // Remove any connections involving this marker
    setConnections((prevConnections) =>
      prevConnections.filter((conn) => conn.from !== id && conn.to !== id)
    );
    setShowBeaconNameDropdownForMarkerId(null); // Close beacon name dropdown if marker deleted
  };

  const handleImageClick = (event) => {
    if (justFinishedDragRef.current) {
      justFinishedDragRef.current = false;
      return;
    }
    console.log("[ImageMapper] handleImageClick triggered"); // DEBUG
    if (
      !imgRef.current ||
      event.target.closest(".marker-interaction") ||
      potentialDragStart ||
      isDraggingLine
    ) {
      console.log(
        "[ImageMapper] handleImageClick exited early due to conditions."
      ); // DEBUG
      return;
    }

    const imageElement = imgRef.current;
    console.log("[ImageMapper] imageElement:", imageElement); // DEBUG
    const rect = imageElement.getBoundingClientRect();
    const displayedX = event.clientX - rect.left;
    const displayedY = event.clientY - rect.top;
    const naturalWidth = parseFloat(imageElement.dataset.naturalWidth);
    const naturalHeight = parseFloat(imageElement.dataset.naturalHeight);
    const displayedWidth = imageElement.clientWidth;
    const displayedHeight = imageElement.clientHeight;

    console.log(
      "[ImageMapper] Coords - displayed:",
      { displayedX, displayedY },
      "natural:",
      { naturalWidth, naturalHeight },
      "client:",
      { displayedWidth, displayedHeight }
    ); // DEBUG

    if (
      isNaN(naturalWidth) ||
      naturalWidth === 0 ||
      isNaN(naturalHeight) ||
      naturalHeight === 0
    ) {
      console.error(
        "[ImageMapper] Natural dimensions are invalid. Cannot plot beacon.",
        { naturalWidth, naturalHeight }
      ); // DEBUG
      return;
    }

    const scaleX = naturalWidth / displayedWidth;
    const scaleY = naturalHeight / displayedHeight;
    const actualX = Math.round(displayedX * scaleX);
    const actualY = Math.round(displayedY * scaleY);

    console.log("[ImageMapper] Calculated actualX, actualY:", {
      actualX,
      actualY,
    }); // DEBUG

    if (markers.length >= availableBeaconsCount) {
      alert(`You can only plot ${availableBeaconsCount} beacons.`);
      return;
    }


    const newMarker = {
      id: nextMarkerId.current,
      x: actualX,
      y: actualY,
      type: "",
      connections: [],
      potentialConnections: [],
      point: "", // Initialize point
      bec_id: null, // Initialize bec_id for available beacon
      bec_name: "", // Initialize bec_name for available beacon
    };
    setMarkers((prevMarkers) => [...prevMarkers, newMarker]);
    setActiveMarkerId(newMarker.id); // New markers still show dropdown immediately
    nextMarkerId.current += 1;

    // Return the new marker's ID to support programmatic row addition
    return newMarker.id;
  };

  const handleMarkerSingleClick = (markerId, event) => {
    event.stopPropagation();

    console.log(
      `[ImageMapper] handleMarkerSingleClick: markerId=${markerId}, currentSeqCount=${clickSequenceRef.current.count}, currentSeqMarker=${clickSequenceRef.current.markerId}`
    ); // DEBUG

    if (
      justFinishedDragRef.current ||
      isDraggingLine ||
      (potentialDragStart && potentialDragStart.marker.id !== markerId)
    ) {
      console.log(
        "[ImageMapper] handleMarkerSingleClick exited early due to drag/potentialDrag conditions."
      ); // DEBUG
      return;
    }

    // Clear any existing timeout from a previous click in the sequence
    if (clickSequenceRef.current.timer) {
      clearTimeout(clickSequenceRef.current.timer);
      clickSequenceRef.current.timer = null; // Explicitly nullify after clearing
    }

    // If it's a new marker or a different marker, reset sequence
    if (clickSequenceRef.current.markerId !== markerId) {
      clickSequenceRef.current.markerId = markerId;
      clickSequenceRef.current.count = 1;
    } else {
      // Same marker, increment count
      clickSequenceRef.current.count += 1;
    }

    console.log(
      `[ImageMapper] Updated click sequence: count=${clickSequenceRef.current.count}, markerId=${clickSequenceRef.current.markerId}`
    ); // DEBUG

    if (clickSequenceRef.current.count === 3) {
      // Triple-click for removal
      console.log(
        `[ImageMapper] Triple click detected for marker ${markerId}. Setting for removal confirmation.`
      ); // DEBUG
      setMarkerIdForRemovalConfirmation(markerId);
      setActiveMarkerId(null); // Hide dropdown if it was open
      setShowBeaconNameDropdownForMarkerId(null); // Hide beacon name dropdown
      // Reset sequence immediately after triple click action
      clickSequenceRef.current = { markerId: null, count: 0, timer: null };
    } else if (clickSequenceRef.current.count === 2) {
      // Double-click to activate
      console.log(
        `[ImageMapper] Double click detected for marker ${markerId}. Activating marker.`
      ); // DEBUG
      setActiveMarkerId(markerId);
      setMarkerIdForRemovalConfirmation(null);
      setShowBeaconNameDropdownForMarkerId(null); // Hide beacon name dropdown
    } else {
      // Single click (count === 1)
      setShowBeaconNameDropdownForMarkerId(null); // Hide beacon name dropdown on single click
      clickSequenceRef.current.timer = setTimeout(() => {
        console.log(
          `[ImageMapper] Timeout clearing click sequence for markerId (timeout scope): ${markerId}. Current ref state: markerId=${clickSequenceRef.current.markerId}, count=${clickSequenceRef.current.count}`
        ); // DEBUG
        if (clickSequenceRef.current.markerId === markerId) {
          clickSequenceRef.current = { markerId: null, count: 0, timer: null };
          console.log(
            `[ImageMapper] Click sequence reset by timeout for markerId: ${markerId}`
          ); // DEBUG
        }
      }, TRIPLE_CLICK_TIMEOUT);
    }
  };

  const handleBeaconTypeChange = (markerId, typeId) => {
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) =>
        marker.id === markerId ? { ...marker, type: typeId } : marker
      )
    );
    setActiveMarkerId(null);
    setMarkerIdForRemovalConfirmation(null); // Close removal confirmation if open
    setConnectionIdForRemovalConfirmation(null); // Close connection removal confirmation if open
    setShowBeaconNameDropdownForMarkerId(null); // Close beacon name dropdown

    // Trigger point input popup after type selection
    setPointInputMarkerId(markerId);
  };

  // Handle updating a connection's properties
  const handleUpdateConnection = (connectionId, field, value) => {
    setConnections((prevConnections) =>
      prevConnections.map((conn) =>
        conn.id === connectionId ? { ...conn, [field]: value } : conn
      )
    );
  };

  // Handle adding a new row to the connections table
  const handleAddConnectionRow = () => {
    const newId =
      connections.length > 0
        ? Math.max(...connections.map((c) => c.id)) + 1
        : 1;
    const newConnection = {
      id: newId,
      from: "",
      to: "",
      distance_meters: 0,
      nav_directions_id: 1,
      level: 1,
    };
    setConnections((prev) => [...prev, newConnection]);
    return newId;
  };

  // Handle deleting a connection
  const handleDeleteConnection = (connectionId) => {
    console.log("Deleting connection:", connectionId);
    setConnections((prevConnections) => {
      const updatedConnections = prevConnections.filter(
        (conn) => conn.id !== connectionId
      );
      console.log("Updated connections after deletion:", updatedConnections);
      return updatedConnections;
    });
  };

  const confirmRemoveMarker = (markerIdToRemove) => {
    setMarkers((prevMarkers) => {
      // Filter out the marker to be removed
      const newMarkers = prevMarkers.filter((m) => m.id !== markerIdToRemove);

      // Renumber the remaining markers to maintain sequential order
      return newMarkers.map((marker, index) => ({
        ...marker,
        id: index + 1,
        source_node: `B${String(index + 1).padStart(3, "0")}`,
      }));
    });

    // Update connections to reflect the new marker IDs
    setConnections((prevConnections) => {
      // First remove connections to/from the deleted marker
      const filteredConnections = prevConnections.filter(
        (conn) => conn.from !== markerIdToRemove && conn.to !== markerIdToRemove
      );

      // Then update connection references to account for renumbered markers
      return filteredConnections.map((conn) => {
        let newFrom = conn.from;
        let newTo = conn.to;

        // If a marker's ID was greater than the removed marker, decrement it
        if (conn.from > markerIdToRemove) newFrom = conn.from - 1;
        if (conn.to > markerIdToRemove) newTo = conn.to - 1;

        return {
          ...conn,
          from: newFrom,
          to: newTo,
          id: `${newFrom}-${newTo}`, // Update connection ID
        };
      });
    });

    // Update the nextMarkerId to be one more than the highest current ID
    setMarkers((prevMarkers) => {
      const maxId =
        prevMarkers.length > 0
          ? Math.max(...prevMarkers.map((marker) => marker.id))
          : 0;
      nextMarkerId.current = maxId + 1;
      return prevMarkers;
    });

    // Reset active marker if it was the one being removed
    if (activeMarkerId === markerIdToRemove) {
      setActiveMarkerId(null);
    }

    // Reset removal confirmation state
    setMarkerIdForRemovalConfirmation(null);
    setShowBeaconNameDropdownForMarkerId(null); // Close beacon name dropdown
  };

  const cancelRemoveMarker = () => {
    setMarkerIdForRemovalConfirmation(null);
    if (clickSequenceRef.current.timer) {
      clearTimeout(clickSequenceRef.current.timer);
    }
    clickSequenceRef.current = { markerId: null, count: 0, timer: null }; // Reset sequence on cancel
  };

  const handleConnectionLineClick = (connectionId, event) => {
    event.stopPropagation(); // Prevent click from bubbling to image container
    if (markerIdForRemovalConfirmation !== null) {
      setMarkerIdForRemovalConfirmation(null);
    }
    if (connectionIdForRemovalConfirmation === connectionId) {
      return;
    }
    setConnectionIdForRemovalConfirmation(connectionId);
    setActiveMarkerId(null); // Close any open beacon dropdown
    setShowBeaconNameDropdownForMarkerId(null); // Close beacon name dropdown
  };

  const confirmRemoveConnection = (connectionIdToRemove) => {
    setConnections((prevConns) =>
      prevConns.filter((c) => c.id !== connectionIdToRemove)
    );
    setConnectionIdForRemovalConfirmation(null);
  };

  const cancelRemoveConnection = () => {
    setConnectionIdForRemovalConfirmation(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (markerIdForRemovalConfirmation !== null) {
        const markerDiv = document.getElementById(
          `marker-div-${markerIdForRemovalConfirmation}`
        );
        const popupDiv = document.getElementById(
          `remove-confirm-popup-${markerIdForRemovalConfirmation}`
        );

        if (
          markerDiv &&
          popupDiv &&
          !markerDiv.contains(event.target) &&
          !popupDiv.contains(event.target)
        ) {
          if (!event.target.closest(".marker-interaction")) {
            cancelRemoveMarker();
          }
        }
      }
      // Also handle closing the beacon name dropdown if clicked outside
      if (showBeaconNameDropdownForMarkerId !== null) {
        const beaconNameDropdown = document.getElementById(`beacon-name-dropdown-${showBeaconNameDropdownForMarkerId}`);
        if (beaconNameDropdown && !beaconNameDropdown.contains(event.target)) {
          setShowBeaconNameDropdownForMarkerId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [markerIdForRemovalConfirmation, showBeaconNameDropdownForMarkerId]); // Add showBeaconNameDropdownForMarkerId to dependencies

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (connectionIdForRemovalConfirmation !== null) {
        const popupDiv = document.getElementById(
          `remove-conn-popup-${connectionIdForRemovalConfirmation}`
        );
        if (
          popupDiv &&
          !popupDiv.contains(event.target) &&
          !event.target.closest(".connection-line")
        ) {
          if (!event.target.closest(".marker-interaction")) {
            cancelRemoveConnection();
          }
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [connectionIdForRemovalConfirmation]);

  const getMarkerDisplayPosition = (marker) => {
    const displayX =
      (marker.x / parseFloat(imgRef.current?.dataset.naturalWidth || 1)) *
      imageDimensions.width;
    const displayY =
      (marker.y / parseFloat(imgRef.current?.dataset.naturalHeight || 1)) *
      imageDimensions.height;
    let markerBgColor = "bg-red-500";
    if (activeMarkerId === marker.id) markerBgColor = "bg-yellow-400";
    return { displayX, displayY, markerBgColor };
  };

  const calculateDistance = (marker1, marker2) => {
    const dx = marker1.x - marker2.x;
    const dy = marker1.y - marker2.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleConnectAllNeighbors = () => {
    if (markers.length < 2) {
      alert("Need at least two markers to connect neighbors.");
      return;
    }

    const newConnections = [];
    const existingConnectionIds = new Set(connections.map((c) => c.id));

    markers.forEach((sourceMarker) => {
      const potentialNeighbors = markers
        .filter((targetMarker) => targetMarker.id !== sourceMarker.id)
        .map((targetMarker) => ({
          marker: targetMarker,
          distance: calculateDistance(sourceMarker, targetMarker),
        }))
        .sort((a, b) => a.distance - b.distance);

      let connectionsMadeForThisMarker = 0;
      for (const neighborInfo of potentialNeighbors) {
        if (connectionsMadeForThisMarker >= parseInt(maxNeighborsToConnect, 10))
          break;

        const targetMarker = neighborInfo.marker;
        const id1 = Math.min(sourceMarker.id, targetMarker.id);
        const id2 = Math.max(sourceMarker.id, targetMarker.id);
        const connectionId = `${id1}-${id2}`;

        if (
          !existingConnectionIds.has(connectionId) &&
          !newConnections.some((nc) => nc.id === connectionId)
        ) {
          newConnections.push({
            id: connectionId,
            from: sourceMarker.id,
            to: targetMarker.id,
          });
          existingConnectionIds.add(connectionId);
          connectionsMadeForThisMarker++;
        }
      }
    });

    if (newConnections.length > 0) {
      setConnections((prevConnections) => [
        ...prevConnections,
        ...newConnections,
      ]);
    }
  };

  const handleClearAllConnections = () => {
    setConnections([]);
  };

  const getMaxConnections = () => parseInt(maxNeighborsToConnect, 10) || 0;

  const getBeaconTableData = () => {
    const maxAllowedConnections = getMaxConnections();
    const becHexIdentifier = "0x";
    const statusId = "1";

    const padToFour = (numStr) => String(numStr).padStart(4, "0");
    const padToTwo = (val) => String(val).padStart(2, "0");

    return markers.map((marker) => {
      const numericX = marker.x !== undefined ? Number(marker.x) : 0;
      const numericY = marker.y !== undefined ? Number(marker.y) : 0;

      const rawTypeId =
        marker.type !== undefined && marker.type !== null
          ? String(marker.type)
          : "";

      const paddedBeaconTypeId = padToTwo(rawTypeId);
      const paddedX = padToFour(numericX.toFixed(0));
      const paddedY = padToFour(numericY.toFixed(0));
      const floorIdToUse =
        selectedFloorId && String(selectedFloorId).trim() !== ""
          ? String(selectedFloorId)
          : "0";
      const paddedFloorId = padToTwo(floorIdToUse);
      const becInstanceKey = `${becHexIdentifier}${paddedFloorId}${paddedBeaconTypeId}${paddedX}${paddedY}`;

      const sourceNodeValue = String(marker.id);
      const actualNeighborIds = connections
        .filter((conn) => conn.from === marker.id || conn.to === marker.id)
        .map((conn) => String(conn.from === marker.id ? conn.to : conn.from));
      const neighborsNodeValue =
        actualNeighborIds.length > 0 ? actualNeighborIds.join(", ") : "";

      return {
        id: marker.id,
        source_node: sourceNodeValue,
        neighbors_node: neighborsNodeValue,
        bec_hex_identifier: becHexIdentifier,
        bec_floor_id: paddedFloorId,
        bec_typ_id: paddedBeaconTypeId,
        bex_pt_map_X: paddedX,
        bex_pt_map_Y: paddedY,
        status_id: statusId,
        bec_instance_key: becInstanceKey,
        points: marker.point || "",
        bec_id: marker.bec_id || "", // Include bec_id
        bec_name: marker.bec_name || "", // Include bec_name
      };
    });
  };

  const getBeaconTypeOptions = () => {
    return Object.entries(beaconTypes).map(([id, name]) => (
      <option key={id} value={id}>
        {name}
      </option>
    ));
  };

  const handleDownloadPackage = async () => {
    console.log("handleDownloadPackage function CALLED!");

    console.log("Initial check - canvasRef.current:", canvasRef.current);
    console.log("Initial check - imageSrc:", imageSrc);
    const tableData = getBeaconTableData();
    console.log("Initial check - tableData length:", tableData.length);

    if (tableData.length === 0 && (!canvasRef.current || !imageSrc)) {
      console.log("Condition for 'No data or image to download' MET.");
      alert("No data or image to download.");
      return;
    }

    const zip = new JSZip();
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(
      now.getMonth() + 1
    ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(
      now.getHours()
    ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
      now.getSeconds()
    ).padStart(2, "0")}`;

    // 1. Prepare Excel file
    const headers = [
      "Marker ID",
      "Source Node",
      "Neighbor(s) Node",
      "bec_hex_identifier",
      "bec_floor_id",
      "bec_typ_id",
      "bex_pt_map_X",
      "bex_pt_map_Y",
      "status_id",
      "bec_instance_key",
      "points", // Add points to headers
      "bec_id", // Add bec_id to headers
      "bec_name", // Add bec_name to headers
    ];
    const dataForExcel = [
      headers,
      ...tableData.map((row) => [
        row.id,
        row.source_node,
        row.neighbors_node,
        row.bec_hex_identifier,
        row.bec_floor_id,
        row.bec_typ_id,
        row.bex_pt_map_X,
        row.bex_pt_map_Y,
        row.status_id,
        row.bec_instance_key,
        row.points, // Add points to data
        row.bec_id, // Add bec_id to data
        row.bec_name, // Add bec_name to data
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(dataForExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BeaconData");
    const excelArrayBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });
    zip.file("BeaconTableData.xlsx", excelArrayBuffer);

    // 3. Generate and Download Zip
    if (Object.keys(zip.files).length > 0) {
      zip
        .generateAsync({ type: "blob" })
        .then(function (content) {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(content);
          link.download = `BeaconExport_${timestamp}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href); // Clean up
        })
        .catch((err) => {
          console.error("Error generating zip file:", err);
          alert("Error generating zip file.");
        });
    } else {
      alert("Nothing to download.");
    }
  };

  const handleDownloadImage = () => {
    if (!imgRef.current) return;

    const img = imgRef.current;
    const naturalWidth = parseInt(img.dataset.naturalWidth, 10);
    const naturalHeight = parseInt(img.dataset.naturalHeight, 10);

    const canvas = document.createElement("canvas");
    canvas.width = naturalWidth;
    canvas.height = naturalHeight;
    const ctx = canvas.getContext("2d");

    // 1. Draw the original image
    ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight);

    // 2. Draw connections
    ctx.strokeStyle = "#FF0000"; // Red color for connections
    ctx.lineWidth = Math.max(2, 2 * (naturalWidth / imageDimensions.width)); // Scale line width, with a minimum

    connections.forEach((conn) => {
      const fromMarker = markers.find((m) => m.id === conn.from);
      const toMarker = markers.find((m) => m.id === conn.to);
      if (fromMarker && toMarker) {
        ctx.beginPath();
        ctx.moveTo(fromMarker.x, fromMarker.y);
        ctx.lineTo(toMarker.x, toMarker.y);
        ctx.stroke();
      }
    });

    // 3. Draw markers
    markers.forEach((marker) => {
      const markerRadius = Math.max(
        5,
        10 * (naturalWidth / imageDimensions.width)
      ); // Scale marker size

      // Draw circle for marker
      ctx.beginPath();
      ctx.arc(marker.x, marker.y, markerRadius, 0, 2 * Math.PI, false);
      ctx.fillStyle = "#3b82f6"; // Blue color for markers
      ctx.fill();
      ctx.lineWidth = Math.max(1, 2 * (naturalWidth / imageDimensions.width));
      ctx.strokeStyle = "#FFFFFF"; // White border
      ctx.stroke();

      // Draw marker ID text
      const fontSize = Math.max(
        12,
        12 * (naturalWidth / imageDimensions.width)
      );
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.fillStyle = "#FFFFFF";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(marker.id, marker.x, marker.y);
    });

    // 4. Trigger download
    const link = document.createElement("a");
    link.download = "floor-plan-with-beacons.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };



  return (
    <BeaconMapperView
      markers={markers}
      connections={connections}
      imageSrc={imageSrc}
      imageDimensions={imageDimensions}
      imgRef={imgRef}
      imageContainerRef={imageContainerRef}
      maxNeighborsToConnect={maxNeighborsToConnect}
      activeMarkerId={activeMarkerId}
      markerIdForRemovalConfirmation={markerIdForRemovalConfirmation}
      connectionIdForRemovalConfirmation={connectionIdForRemovalConfirmation}
      dragStartMarker={dragStartMarker}
      currentDragLineEnd={currentDragLineEnd}
      isDraggingLine={isDraggingLine}
      selectedFloorId={selectedFloorId}
      showClearAllConfirmation={showClearAllConfirmation}
      beaconTypes={beaconTypes}
      getBeaconTableData={getBeaconTableData}
      getMarkerDisplayPosition={getMarkerDisplayPosition}
      getBeaconTypeOptions={getBeaconTypeOptions}
      handleClearAllClick={handleClearAllClick}
      setShowClearAllConfirmation={setShowClearAllConfirmation}
      clearAllBeacons={clearAllBeacons}
      handleImageUpload={handleImageUpload}
      handleConnectAllNeighbors={handleConnectAllNeighbors}
      handleClearAllConnections={handleClearAllConnections}
      handleImageClick={handleImageClick}
      handleImageContainerMouseMove={handleImageContainerMouseMove}
      handleImageContainerMouseUp={handleImageContainerMouseUp}
      handleDownloadImage={handleDownloadImage}
      handleAddRow={handleAddRow}
      handleUpdateMarker={handleUpdateMarker}
      handleDeleteMarker={handleDeleteMarker}
      handleUpdateConnection={handleUpdateConnection}
      handleAddConnectionRow={handleAddConnectionRow}
      handleDeleteConnection={handleDeleteConnection}
      handleMarkerMouseDown={handleMarkerMouseDown}
      handleMarkerMouseUp={handleMarkerMouseUp}
      handleMarkerSingleClick={handleMarkerSingleClick}
      handleBeaconTypeChange={handleBeaconTypeChange}
      confirmRemoveMarker={confirmRemoveMarker}
      cancelRemoveMarker={cancelRemoveMarker}
      confirmRemoveConnection={confirmRemoveConnection}
      cancelRemoveConnection={cancelRemoveConnection}
      handleConnectionLineClick={handleConnectionLineClick}
      setMaxNeighborsToConnect={setMaxNeighborsToConnect}
      pointInputMarkerId={pointInputMarkerId}
      handlePointValueSubmit={handlePointValueSubmit}
      availableBeaconsData={availableBeaconsData} 
      showBeaconNameDropdownForMarkerId={showBeaconNameDropdownForMarkerId} 
      handleBeaconNameChange={handleBeaconNameChange} 
     
    />
  );
};

export default ImageMapper;
